import { AnalyticService } from '@/services/AnalyticService';
import { FolderService } from '@/services/FolderService';
import type { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import type { UploadFile } from 'antd/es/upload';
import { ofType } from 'redux-observable';
import { EMPTY, from, interval, merge, Observable, of } from 'rxjs';
import { catchError, concatMap, delay, filter, map, mergeMap, switchMap, takeWhile } from 'rxjs/operators';
import { fileActions } from '../file';
import type { RootEpic } from '../types';
import {
    setFiles,
    updateFileProgress,
    uploadActions
} from './uploadSlice';
import { uploadFileActions } from '../uploadFiles/uploadFileSlice';

// Giả sử response trả về có dạng { success: boolean, fileId: string, cloundLink: string }
const uploadFileApiWithProgress = (
    file: UploadFile,
    companyId: string,
    labelId: string,
    isPublish: boolean
): Observable<{ type: 'progress' | 'complete'; progress?: number; response?: any }> => {
    return new Observable(observer => {
        const formData = new FormData();
        if (file.originFileObj) {
            formData.append('iFile', file.originFileObj, file.name);
        }

        // Progress callback sẽ emit progress events
        const progressCallback = (percent: number) => {
            observer.next({
                type: 'progress',
                progress: percent
            });
        };

        FolderService.Post.uploadFile(
            formData,
            progressCallback,
            companyId,
            labelId,
            isPublish
        ).subscribe({
            next: (response) => {
                // HttpClient.upload trả về response cuối cùng khi upload hoàn tất
                observer.next({
                    type: 'complete',
                    response: {
                        success: true,
                        fileId: response.fileId,
                        cloudLink: response.cloudLink,
                        id: response.id
                    }
                });
                observer.complete();
            },
            error: (err) => {
                observer.error(err);
            }
        });
    });
};

export const uploadEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        ofType(setFiles.type),
        mergeMap(
            (
                action: PayloadAction<{
                    fileList: UploadFile[];
                    companyId: string;
                    labelId: string;
                    isPublish: boolean;
                }>
            ) => {
                const { fileList, companyId, labelId, isPublish } = action.payload;

                // Tách fileList thành 2 nhóm
                const revitFiles = fileList.filter(file =>
                    file.name.toLowerCase().endsWith('.rvt')
                );
                const otherFiles = fileList.filter(file =>
                    !file.name.toLowerCase().endsWith('.rvt')
                );

                const actions: any[] = [];

                // Nếu có file Revit, dispatch uploadFilesRequest
                if (revitFiles.length > 0) {
                    // Chuyển về kiểu File nếu cần (UploadFile.originFileObj là File)
                    const files = revitFiles
                        .map(f => f.originFileObj)
                        .filter(Boolean) as File[];
                    actions.push(
                        uploadFileActions.uploadFilesRequest({
                            files,
                            project: labelId // hoặc truyền projectId phù hợp
                        })
                    );
                }

                // Các file còn lại xử lý như cũ
                const otherFiles$ = from(otherFiles).pipe(
                    mergeMap((file) =>
                        uploadFileApiWithProgress(
                            file,
                            companyId,
                            labelId,
                            isPublish
                        ).pipe(
                            mergeMap((event) => {
                                if (event.type === 'progress') {
                                    return of(updateFileProgress({
                                        fileName: file.name,
                                        progress: event.progress!,
                                    }));
                                } else if (event.type === 'complete') {
                                    const actions = [];
                                    if (event.response?.id) {
                                        actions.push(
                                            fileActions.getDocumentByIdRequest({
                                                id: event.response.id,
                                                file
                                            })
                                        );
                                    }
                                    actions.push(
                                        uploadActions.setUploadResponse({
                                            uid: file.uid,
                                            response: event.response
                                        })
                                    );
                                    actions.push(
                                        fileActions.getAllFileContent(labelId)
                                    );
                                    return from(actions);
                                }
                                return of();
                            }),
                            catchError((error) => {
                                return from([
                                    updateFileProgress({
                                        fileName: file.name,
                                        progress: -1,
                                    }),
                                    fileActions.getAllFileContent(labelId)
                                ]);
                            })
                        )
                    )
                );

                // Trả về các actions: uploadFilesRequest cho Revit + xử lý các file còn lại
                return merge(
                    from(actions),
                    otherFiles$
                );
            }
        )
    );

// Giữ nguyên các epic khác
export const processEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(uploadActions.extractFileRequest.match),
        concatMap((action) => {
            const { guid_file, path_file } = action.payload;

            return AnalyticService.Post.extractFileByPathFile({
                guid_file,
                path_file,
            }).pipe(
                switchMap((res) => {
                    // Poll trạng thái riêng cho file này
                    const poll$ = interval(700).pipe(
                        switchMap(() =>
                            AnalyticService.Get.getTaskByGuidFile(guid_file).pipe(
                                switchMap((statusRes) => {
                                    const actions: any[] = [
                                        uploadActions.setUploadProcess({ guid_file, process: statusRes }),
                                        uploadActions.getFileStatusSuccess(statusRes),
                                    ];

                                    if (statusRes.status === 'success' || statusRes.status === 'failed') {
                                        actions.push(
                                            uploadActions.processCompleted({
                                                guid_file,
                                                status: statusRes.status
                                            })
                                        );
                                    }

                                    return of(...actions);
                                })

                            )
                        ),
                        takeWhile(
                            (action) =>
                                !(
                                    action.type ===
                                    uploadActions.getFileStatusSuccess
                                        .type &&
                                    (action.payload.status === "success" ||
                                        action.payload.status === "failed")
                                ),
                            true
                        )
                    );

                    return merge(
                        of(
                            uploadActions.setUploadStatus({
                                guid_file,
                                status: res,
                            }),
                            uploadActions.extractFileSuccess(res)
                        ),
                        poll$
                    );
                }),
                catchError((err) => {
                    console.error("Error extracting file", err);
                    return of(
                        uploadActions.extractFileFailure(
                            err.message || "Error extracting file"
                        )
                    );
                }),
                delay(2000)
            );
        })
    );

export const getFileStatusByGuidFile$: RootEpic = (action$) =>
    action$.pipe(
        filter(uploadActions.getFileStatusRequest.match),
        switchMap((action) =>
            AnalyticService.Get.getTaskByGuidFile(action.payload).pipe(
                map((res) => uploadActions.getFileStatusSuccess(res)),
                catchError((err) =>
                    of(uploadActions.getFileStatusFailure(err.message || "Error fetching file status"))
                )
            )
        )
    );

export const viewAnalyticEpic$: RootEpic = (action$) =>
    action$.pipe(
        filter((action) => action.type === "view-analytic"),
        map((action: any) => {
            const item = action.payload;
            if (!item) return { type: "docai/viewAnalyticSkipped" };

            const analyticUrl = `https://acsd.hicas.vn/pdf/view?path=https://acsd.hicas.vn/file/view-pdf-guid/${item.baseFileId}&filename=${item.filename}&baseFileId=${item.baseFileId}&baseMajorRev=${item.baseMajorRev}&baseMinorRev=${item.baseMinorRev}`;
            window.open(analyticUrl, "_blank");

            return { type: "docai/viewAnalyticOpened" };
        })
    );

export const autoExtractEpic$: RootEpic = (action$, state$) =>
    action$.pipe(
        ofType(
            uploadActions.setUploadResponse.type, // Khi có file mới upload xong hoặc getDocumentById trả về
            uploadActions.processCompleted.type, // Khi extract thất bại
            uploadActions.getFileStatusSuccess.type, // Khi nhận được status update
        ),
        concatMap(() => {
            const { fileStatusList } = state$.value.upload;

            // Lọc các file cần xử lý
            const needProcess = fileStatusList.filter((item) => {
                const hasUploadResponse = item.uploadResponse;
                const hasCloudLink = item.uploadResponse?.cloudLink;
                const hasFileId = item.uploadResponse?.fileId;
                const noUploadStatus = !item.uploadStatus;
                const notProcessing =
                    !item.uploadProcess ||
                    (item.uploadProcess.status !== 'processing' &&
                        item.uploadProcess.status !== 'pending');

                const fileName = item.file.name?.toLowerCase() || "";
                const isPdfFile = fileName.endsWith(".pdf");
                // Nếu không phải PDF và đã có uploadProcess thì bỏ qua
                if (!isPdfFile && item.uploadProcess) {
                    return false;
                }

                return (
                    hasUploadResponse &&
                    hasCloudLink &&
                    hasFileId &&
                    noUploadStatus &&
                    notProcessing
                );
            });
            // Nếu không có file nào cần xử lý, return EMPTY
            if (needProcess.length === 0) {
                return EMPTY;
            }

            // Lấy file đầu tiên để xử lý (xử lý tuần tự)
            const file = needProcess[0];
            const isPdfFile = file.file.name?.toLowerCase().endsWith('.pdf');

            if (!isPdfFile) {
                // Nếu không phải PDF, set uploadProcess.status = 'success' ngay lập tức
                return of(
                    uploadActions.setUploadProcess({
                        guid_file: file.uploadResponse!.fileId,
                        process: {
                            _id: '',
                            irai_id: '',
                            guid_file: file.uploadResponse!.fileId,
                            type: 'non-pdf',
                            sum_page: 0,
                            current_page: 0,
                            status: 'success',
                            verify: true
                        }
                    })
                );
            }

            // Nếu là PDF, tiến hành extract
            return of(
                uploadActions.extractFileRequest({
                    guid_file: file.uploadResponse!.fileId,
                    path_file: file.uploadResponse!.cloudLink,
                })
            ).pipe(
                delay(800) // Thêm delay nhỏ để tránh spam API
            );
        })
    );

export const uploadEpics = [
    processEpic$,
    uploadEpic$,
    viewAnalyticEpic$,
    getFileStatusByGuidFile$,
    autoExtractEpic$
];